import React from 'react';
import { Info, Search, List, Tag, Briefcase, Key, FileText, Square } from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';

const KeywordToolkit = () => {
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-6 py-12 text-gray-800">
        {/* Header */}
        <h1 className="text-4xl font-bold mb-8">Resume Keyword Toolkit</h1>

        {/* Info Box */}
        <div className="bg-gray-100 p-6 rounded-lg mb-12">
          <div className="flex items-start gap-3 mb-4">
            <Info className="w-5 h-5 mt-1" />
            <div>
              <h2 className="font-medium mb-2">Essential Overview</h2>
              <p className="text-gray-700">
                Boost your resume's visibility and ensure it stands out by incorporating the right keywords. 
                Use this toolkit to identify and integrate powerful keywords that align with your desired 
                job roles and industries.
              </p>
            </div>
          </div>
        </div>

        {/* Why Keywords Matter */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Key className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">1. Why Keywords Matter</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Keywords help your resume pass Applicant Tracking Systems (ATS) used by many employers</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>They highlight your relevant skills and experiences to hiring managers</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Including the right keywords increases your chances of being shortlisted for interviews</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Where to Find Keywords */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Search className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">2. Where to Find Keywords</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span><strong>Job Descriptions:</strong> Review the responsibilities and qualifications listed in job postings</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span><strong>Industry Websites:</strong> Look for common jargon, certifications, and skillsets</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span><strong>Professional Profiles:</strong> Analyze LinkedIn profiles of professionals in similar roles</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Common Keyword Categories */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <List className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">3. Common Keyword Categories</h3>
              <ul className="space-y-4">
                <li className="bg-white p-4 rounded-lg">
                  <p className="font-medium text-blue-800 mb-2">Technical Skills</p>
                  <p className="text-gray-600">Programming languages, software, tools (e.g., Python, Salesforce, Excel)</p>
                </li>
                <li className="bg-white p-4 rounded-lg">
                  <p className="font-medium text-blue-800 mb-2">Industry-Specific Terms</p>
                  <p className="text-gray-600">Words or phrases unique to your field (e.g., Agile, FDA compliance)</p>
                </li>
                <li className="bg-white p-4 rounded-lg">
                  <p className="font-medium text-blue-800 mb-2">Soft Skills</p>
                  <p className="text-gray-600">Communication, leadership, collaboration</p>
                </li>
                <li className="bg-white p-4 rounded-lg">
                  <p className="font-medium text-blue-800 mb-2">Certifications and Training</p>
                  <p className="text-gray-600">PMP, Six Sigma, CPA</p>
                </li>
                <li className="bg-white p-4 rounded-lg">
                  <p className="font-medium text-blue-800 mb-2">Action Verbs</p>
                  <p className="text-gray-600">Managed, developed, increased, reduced</p>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* How to Integrate Keywords */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Tag className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">4. How to Integrate Keywords</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Headline and Summary</h4>
                  <div className="bg-white p-4 rounded-lg mb-2">
                    <p className="text-gray-600 italic">Example: "Certified Digital Marketing Specialist with Expertise in SEO and PPC"</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Work Experience</h4>
                  <div className="bg-white p-4 rounded-lg mb-2">
                    <p className="text-gray-600 italic">Example: "Increased organic traffic by 40% through SEO optimization."</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Additional Sections</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                      <span>List both hard and soft skills relevant to the job in Skills Section</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                      <span>Highlight relevant training programs and certifications in Education Section</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Example Keywords by Industry */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Briefcase className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">5. Example Keywords by Industry</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Marketing</h4>
                  <p className="text-gray-600">SEO, PPC, content marketing, analytics, campaign management, branding</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Technology</h4>
                  <p className="text-gray-600">Python, JavaScript, cloud computing, cybersecurity, DevOps, database management</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Healthcare</h4>
                  <p className="text-gray-600">Patient care, HIPAA compliance, clinical trials, EHR, medical coding, diagnostics</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Finance</h4>
                  <p className="text-gray-600">Budgeting, financial modeling, risk management, forecasting, auditing, investments</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <FileText className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">6. Tips for Effective Keyword Use</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span><strong>Avoid Keyword Stuffing:</strong> Use keywords naturally and contextually</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span><strong>Match Exact Phrases:</strong> Use terminology as listed in the job description (e.g., "project management" vs. "managing projects")</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span><strong>Stay Current:</strong> Update your resume regularly to reflect the latest trends and terminology in your field</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Closing Note */}
        <div className="bg-gray-100 p-6 rounded-lg">
          <p className="text-gray-700">
            By following this toolkit, you'll ensure your resume is optimized for ATS and impresses hiring managers. 
            Start using targeted keywords today to elevate your job search success!
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default KeywordToolkit;